import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  Clock,
  DollarSign,
  Package,
  Users,
  TrendingUp,
  Calendar,
  Activity,
  AlertCircle,
  CheckCircle,
  Mic,
  BrainCircuit,
  FileText,
  BarChart3,
  Zap,
  Shield,
  RefreshCw,
  Target,
  TrendingDown,
  Eye,
  Upload
} from 'lucide-react';
import { Link } from 'react-router-dom';
import MetricCard from '../Widgets/MetricCard';
import KPIChart from '../Charts/KPIChart';
import AlertBanner from '../Widgets/AlertBanner';
import { managerService } from '../../services/managerService';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { format } from 'date-fns';
import TekMetricDashboard from '../TekMetric/TekMetricDashboard';

interface AIInsight {
  id: string;
  type: 'recommendation' | 'alert' | 'opportunity';
  title: string;
  description: string;
  impact: string;
  priority: 'high' | 'medium' | 'low';
  action?: string;
}

interface KPIMetric {
  name: string;
  value: number;
  target: number;
  unit: string;
  trend: 'up' | 'down' | 'stable';
  percentageChange: number;
}

const StoreManagerDashboardV2: React.FC = () => {
  const { socket } = useWebSocket();
  const [todayTasks, setTodayTasks] = useState<any[]>([]);
  const [aiInsights, setAIInsights] = useState<AIInsight[]>([]);
  const [isReconciling, setIsReconciling] = useState(false);
  const [reconciliationStatus, setReconciliationStatus] = useState<string>('');
  const [selectedTimeRange, setSelectedTimeRange] = useState('today');
  const [kpiMetrics, setKPIMetrics] = useState<KPIMetric[]>([]);

  const { data: dashboardData, isLoading, refetch } = useQuery({
    queryKey: ['managerDashboard', selectedTimeRange],
    queryFn: () => managerService.getDashboardData(selectedTimeRange),
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  useEffect(() => {
    if (socket) {
      socket.on('task:update', (tasks) => {
        setTodayTasks(tasks);
      });

      socket.on('ai:insight', (insight: AIInsight) => {
        setAIInsights(prev => [insight, ...prev].slice(0, 5));
      });

      socket.on('reconciliation:status', (status) => {
        setReconciliationStatus(status.message);
        if (status.completed) {
          setIsReconciling(false);
          refetch();
        }
      });

      socket.on('kpi:update', (metrics: KPIMetric[]) => {
        setKPIMetrics(metrics);
      });

      return () => {
        socket.off('task:update');
        socket.off('ai:insight');
        socket.off('reconciliation:status');
        socket.off('kpi:update');
      };
    }
  }, [socket, refetch]);

  const handleReconciliation = async () => {
    setIsReconciling(true);
    setReconciliationStatus('Initializing reconciliation...');
    try {
      await managerService.runReconciliation();
    } catch (error) {
      setIsReconciling(false);
      setReconciliationStatus('Reconciliation failed');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const metrics = dashboardData?.metrics || {};
  const alerts = dashboardData?.alerts || [];
  const weeklyPerformance = dashboardData?.weeklyPerformance || {};
  const vendorMetrics = dashboardData?.vendorMetrics || {};

  return (
    <div className="space-y-6">
      {/* Enhanced Welcome Section with AI Status */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-xl p-6 text-white relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
        <div className="relative z-10">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold mb-2">
                Good {new Date().getHours() < 12 ? 'Morning' : 'Afternoon'}, {dashboardData?.user?.name || 'Manager'}!
              </h1>
              <p className="text-blue-100">
                {format(new Date(), 'EEEE, MMMM d, yyyy')} • Store {dashboardData?.storeLocation || 'Temple'}
              </p>
            </div>
            <div className="flex items-center space-x-2 bg-white/20 rounded-lg px-4 py-2">
              <BrainCircuit className="h-5 w-5" />
              <span className="text-sm font-medium">AI Assistant Active</span>
              <div className="h-2 w-2 bg-green-400 rounded-full animate-pulse"></div>
            </div>
          </div>
          
          <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white/20 backdrop-blur rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Target className="h-5 w-5" />
                <p className="text-sm text-blue-100">Daily Goal Progress</p>
              </div>
              <p className="text-2xl font-bold">{metrics.dailyGoalProgress || 0}%</p>
              <div className="mt-2 h-2 bg-white/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-white transition-all duration-500"
                  style={{ width: `${metrics.dailyGoalProgress || 0}%` }}
                />
              </div>
            </div>
            
            <div className="bg-white/20 backdrop-blur rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Zap className="h-5 w-5" />
                <p className="text-sm text-blue-100">Efficiency Score</p>
              </div>
              <p className="text-2xl font-bold flex items-center">
                {metrics.efficiencyScore || 0}%
                {metrics.efficiencyTrend > 0 ? (
                  <TrendingUp className="h-4 w-4 ml-2" />
                ) : (
                  <TrendingDown className="h-4 w-4 ml-2" />
                )}
              </p>
            </div>
            
            <div className="bg-white/20 backdrop-blur rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Shield className="h-5 w-5" />
                <p className="text-sm text-blue-100">Compliance Status</p>
              </div>
              <p className="text-2xl font-bold flex items-center">
                <CheckCircle className="h-5 w-5 mr-1" />
                {metrics.complianceScore || 100}%
              </p>
            </div>
            
            <div className="bg-white/20 backdrop-blur rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Activity className="h-5 w-5" />
                <p className="text-sm text-blue-100">Active Processes</p>
              </div>
              <p className="text-2xl font-bold">{metrics.activeProcesses || 0}</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* AI Insights Panel */}
      {aiInsights.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center">
              <BrainCircuit className="h-5 w-5 mr-2 text-purple-600" />
              AI Insights & Recommendations
            </h2>
            <button className="text-sm text-purple-600 hover:text-purple-700">
              View All
            </button>
          </div>
          <div className="space-y-3">
            {aiInsights.map((insight) => (
              <div
                key={insight.id}
                className={`p-4 bg-white rounded-lg border-l-4 ${
                  insight.priority === 'high' ? 'border-red-500' :
                  insight.priority === 'medium' ? 'border-yellow-500' :
                  'border-green-500'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      {insight.type === 'alert' && <AlertCircle className="h-4 w-4 text-red-500" />}
                      {insight.type === 'recommendation' && <BrainCircuit className="h-4 w-4 text-purple-500" />}
                      {insight.type === 'opportunity' && <TrendingUp className="h-4 w-4 text-green-500" />}
                      <h3 className="font-medium text-gray-900">{insight.title}</h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{insight.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Impact: {insight.impact}</span>
                      {insight.action && (
                        <button className="text-xs bg-purple-600 text-white px-3 py-1 rounded-full hover:bg-purple-700">
                          {insight.action}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Alerts Section */}
      {alerts.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <AlertBanner alerts={alerts} />
        </motion.div>
      )}

      {/* Time Range Selector */}
      <div className="flex justify-end space-x-2">
        {['today', 'week', 'month'].map((range) => (
          <button
            key={range}
            onClick={() => setSelectedTimeRange(range)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedTimeRange === range
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {range.charAt(0).toUpperCase() + range.slice(1)}
          </button>
        ))}
      </div>

      {/* Enhanced Key Metrics */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        <MetricCard
          title="Revenue"
          value={`$${(metrics.revenue || 0).toLocaleString()}`}
          change={metrics.revenueChange || 0}
          icon={DollarSign}
          trend={metrics.revenueChange > 0 ? 'up' : 'down'}
          color="green"
          subtitle={`Target: $${(metrics.revenueTarget || 0).toLocaleString()}`}
        />
        <MetricCard
          title="Labor Efficiency"
          value={`${metrics.laborEfficiency || 0}%`}
          change={metrics.laborChange || 0}
          icon={Clock}
          trend={metrics.laborChange > 0 ? 'up' : 'down'}
          color="blue"
          subtitle={`${metrics.laborHours || 0} hours used`}
        />
        <MetricCard
          title="Parts Performance"
          value={`${metrics.partsPerformance || 0}%`}
          change={metrics.partsChange || 0}
          icon={Package}
          trend={metrics.partsChange > 0 ? 'up' : 'down'}
          color="purple"
          subtitle={`${metrics.partsSold || 0} units sold`}
        />
        <MetricCard
          title="Team Productivity"
          value={`${metrics.teamProductivity || 0}%`}
          change={metrics.productivityChange || 0}
          icon={Users}
          trend={metrics.productivityChange > 0 ? 'up' : 'down'}
          color="orange"
          subtitle={`${metrics.activeStaff || 0} active staff`}
        />
      </motion.div>

      {/* Quick Actions Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl shadow-lg p-6"
      >
        <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link
            to="/manager/payroll"
            className="flex flex-col items-center justify-center space-y-2 p-6 bg-blue-50 hover:bg-blue-100 rounded-lg transition-all hover:scale-105"
          >
            <Calendar className="h-8 w-8 text-blue-600" />
            <span className="font-medium text-blue-900">Enter Payroll</span>
            <span className="text-xs text-blue-600">Quick Entry</span>
          </Link>
          
          <Link
            to="/manager/voice"
            className="flex flex-col items-center justify-center space-y-2 p-6 bg-purple-50 hover:bg-purple-100 rounded-lg transition-all hover:scale-105"
          >
            <Mic className="h-8 w-8 text-purple-600" />
            <span className="font-medium text-purple-900">Voice Query</span>
            <span className="text-xs text-purple-600">Ask AI</span>
          </Link>
          
          <button
            onClick={handleReconciliation}
            disabled={isReconciling}
            className="flex flex-col items-center justify-center space-y-2 p-6 bg-green-50 hover:bg-green-100 rounded-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isReconciling ? (
              <RefreshCw className="h-8 w-8 text-green-600 animate-spin" />
            ) : (
              <Activity className="h-8 w-8 text-green-600" />
            )}
            <span className="font-medium text-green-900">
              {isReconciling ? 'Reconciling...' : 'Reconcile'}
            </span>
            <span className="text-xs text-green-600">
              {reconciliationStatus || 'Auto-match'}
            </span>
          </button>
          
          <Link
            to="/manager/invoices"
            className="flex flex-col items-center justify-center space-y-2 p-6 bg-orange-50 hover:bg-orange-100 rounded-lg transition-all hover:scale-105"
          >
            <Upload className="h-8 w-8 text-orange-600" />
            <span className="font-medium text-orange-900">Process Invoices</span>
            <span className="text-xs text-orange-600">310x Faster</span>
          </Link>
        </div>
      </motion.div>

      {/* KPI Performance Tracker */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-xl shadow-lg p-6"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">KPI Performance Tracker</h2>
          <button className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-700">
            <Eye className="h-4 w-4" />
            <span>View Details</span>
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {kpiMetrics.length > 0 ? (
            kpiMetrics.map((kpi, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-medium text-gray-900">{kpi.name}</h3>
                  <span className={`text-sm font-medium ${
                    kpi.trend === 'up' ? 'text-green-600' : 
                    kpi.trend === 'down' ? 'text-red-600' : 
                    'text-gray-600'
                  }`}>
                    {kpi.percentageChange > 0 ? '+' : ''}{kpi.percentageChange}%
                  </span>
                </div>
                <div className="flex items-baseline space-x-2">
                  <span className="text-2xl font-bold text-gray-900">
                    {kpi.value}
                  </span>
                  <span className="text-sm text-gray-500">{kpi.unit}</span>
                </div>
                <div className="mt-2">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Target: {kpi.target}</span>
                    <span>{((kpi.value / kpi.target) * 100).toFixed(0)}%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-500 ${
                        kpi.value >= kpi.target ? 'bg-green-500' : 'bg-yellow-500'
                      }`}
                      style={{ width: `${Math.min((kpi.value / kpi.target) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-3 text-center py-8 text-gray-500">
              <BarChart3 className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>KPI metrics will appear here once configured</p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Vendor Performance Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <h2 className="text-lg font-semibold mb-4">Vendor Performance</h2>
          <div className="space-y-3">
            {Object.entries(vendorMetrics).map(([vendor, data]: [string, any]) => (
              <div key={vendor} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`h-10 w-10 rounded-lg bg-${data.color}-100 flex items-center justify-center`}>
                    <FileText className={`h-5 w-5 text-${data.color}-600`} />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">{vendor}</h3>
                    <p className="text-sm text-gray-600">{data.invoices} invoices</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">${data.total.toLocaleString()}</p>
                  <p className={`text-sm ${data.change > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {data.change > 0 ? '+' : ''}{data.change}%
                  </p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <h2 className="text-lg font-semibold mb-4">Today's Priority Tasks</h2>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {todayTasks.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No tasks scheduled for today</p>
            ) : (
              todayTasks.map((task, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border transition-all ${
                    task.completed 
                      ? 'bg-green-50 border-green-200' 
                      : task.priority === 'high'
                      ? 'bg-red-50 border-red-200'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {task.completed ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : task.priority === 'high' ? (
                        <AlertCircle className="h-5 w-5 text-red-600" />
                      ) : (
                        <Clock className="h-5 w-5 text-gray-600" />
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{task.title}</p>
                        <p className="text-sm text-gray-600">{task.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-xs text-gray-500">{task.time}</span>
                      {!task.completed && (
                        <button className="block mt-1 text-xs text-blue-600 hover:text-blue-700">
                          Complete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </motion.div>
      </div>

      {/* Performance Analytics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white rounded-xl shadow-lg p-6"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Performance Analytics</h2>
          <div className="flex items-center space-x-4">
            <select className="text-sm border border-gray-300 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option>Revenue vs Target</option>
              <option>Labor Efficiency</option>
              <option>Parts Sales</option>
              <option>Customer Satisfaction</option>
            </select>
            <div className="flex items-center space-x-2">
              <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-gray-600">Real-time</span>
            </div>
          </div>
        </div>
        <KPIChart
          data={weeklyPerformance}
          type="line"
          height={300}
          showComparison={true}
          comparisonLabel="Last Period"
        />
      </motion.div>

      {/* TekMetric Live Data Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white rounded-xl shadow-lg p-6"
      >
        <TekMetricDashboard />
      </motion.div>

      {/* Recent Activity Feed */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-white rounded-xl shadow-lg p-6"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Recent Activity</h2>
          <div className="flex items-center space-x-2">
            <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-gray-600">Live Feed</span>
          </div>
        </div>
        <div className="space-y-3">
          {dashboardData?.recentActivity?.slice(0, 5).map((activity: any, index: number) => (
            <div key={index} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors">
              <div className="flex items-center space-x-3">
                <div className={`h-10 w-10 rounded-full bg-${activity.color}-100 flex items-center justify-center`}>
                  <Activity className={`h-5 w-5 text-${activity.color}-600`} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                  <p className="text-xs text-gray-600">{activity.user} • {activity.details}</p>
                </div>
              </div>
              <span className="text-xs text-gray-500">{activity.time}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default StoreManagerDashboardV2;